import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, CheckCircle, AlertTriangle, ArrowRight, Video, X, Hand, Loader2, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { scoreBucket, toneClass, toneHex } from '../utils/coachLanguage';
import useMobilityTracker from '../hooks/useMobilityTracker';
import api from '../services/api';
import { toast } from 'sonner';

const sideLabel = (s) => (s === 'L' ? 'LEFT' : s === 'R' ? 'RIGHT' : 'BOTH SIDES');
const ytSearch = (q) => `https://www.youtube.com/results?search_query=${encodeURIComponent(q)}`;

/**
 * AssessmentLiveView — full-screen live-camera mobility capture with skeleton.
 *
 * Reuses the SAME pose engine as the workout camera (via useMobilityTracker)
 * and the SAME held-median scoring math as the upload flow. The user reaches
 * their end-range and HOLDS; a ring fills over ~2.5s and the reading is
 * auto-captured (median of the held window). A manual "Capture now" override
 * and an Accept / Redo gate mean a bad read is never saved.
 *
 * onComplete({ test_id, measurements:[{side, raw_value, guard_violated, guard_peak_violation}] })
 */
const AssessmentLiveView = ({ testDef, onComplete, onUseUpload }) => {
  const tracker = useMobilityTracker();
  const sides = testDef.sides || ['BILATERAL'];
  const [started, setStarted] = useState(false);
  const [sideIdx, setSideIdx] = useState(0);
  const [accepted, setAccepted] = useState([]);
  const [result, setResult] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [slowLoad, setSlowLoad] = useState(false);
  const processedRef = useRef(false);
  const currentSide = sides[sideIdx];

  // Just flip into the camera view — the actual camera start happens in an
  // effect AFTER the <video> has mounted (mirrors the workout camera, which
  // avoids the iOS/Safari race where getUserMedia runs before the element).
  const begin = useCallback(() => {
    processedRef.current = false;
    setSlowLoad(false);
    setStarted(true);
  }, []);

  // Start camera + first test once the full-screen view (and <video>) is live.
  useEffect(() => {
    if (!started) return;
    let cancelled = false;
    (async () => {
      await tracker.startCamera();
      if (!cancelled) tracker.startTest(testDef, sides[sideIdx]);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  // Never leave the user staring at a spinner — if the camera hasn't come up
  // in 12s (and there's no hard error yet), offer the upload fallback.
  useEffect(() => {
    if (!started || tracker.cameraActive || tracker.cameraError) { setSlowLoad(false); return; }
    const t = setTimeout(() => setSlowLoad(true), 12000);
    return () => clearTimeout(t);
  }, [started, tracker.cameraActive, tracker.cameraError]);

  const exit = useCallback(() => {
    try { tracker.stopCamera?.(); } catch (e) { /* noop */ }
    tracker.reset();
    setStarted(false);
    setResult(null);
    setSideIdx(0);
    setAccepted([]);
  }, [tracker]);

  // Stop the camera ONLY when the component actually unmounts. We hold the
  // latest stopCamera in a ref so this effect can have an empty dependency
  // array — depending on `tracker` (a new object each render) would fire the
  // cleanup on every render and kill the camera the instant it starts.
  const stopCameraRef = useRef(null);
  stopCameraRef.current = tracker.stopCamera;
  useEffect(() => () => { try { stopCameraRef.current?.(); } catch (e) { /* noop */ } }, []);

  // When a hold completes, score the measurement (same endpoint as upload).
  useEffect(() => {
    if (tracker.phase !== tracker.PHASES.DONE || processedRef.current) return;
    processedRef.current = true;
    const m = tracker.consumeResult();
    if (!m || m.raw_value == null) {
      toast.error("Didn't get a clean read — let's try that again.");
      processedRef.current = false;
      tracker.startTest(testDef, currentSide);
      return;
    }
    (async () => {
      setScoring(true);
      try {
        const res = await api.scoreMobilityMeasurement({
          test_id: testDef.id,
          side: m.side,
          view_detected: testDef.camera_view || 'side',
          raw_value: m.raw_value,
          guard_violated: m.guard_violated,
          guard_peak_violation: m.guard_peak_violation,
        });
        setResult({ ...res.data, measurement: m });
      } catch (e) {
        setResult({
          raw_value: m.raw_value, score: null,
          primary_label: testDef.primary_metric?.label, measurement: m,
        });
      } finally {
        setScoring(false);
      }
    })();
  }, [tracker.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const accept = () => {
    const m = result.measurement;
    const next = [...accepted, {
      side: m.side, raw_value: m.raw_value,
      neutral_value: m.neutral_value, peak_value: m.peak_value,
      guard_violated: m.guard_violated, guard_peak_violation: m.guard_peak_violation,
    }];
    setResult(null);
    processedRef.current = false;
    if (sideIdx + 1 < sides.length) {
      setAccepted(next);
      const ni = sideIdx + 1;
      setSideIdx(ni);
      tracker.startTest(testDef, sides[ni]);
      toast.success(`${sideLabel(currentSide)} saved — now the ${sideLabel(sides[ni])} side.`);
    } else {
      try { tracker.stopCamera?.(); } catch (e) { /* noop */ }
      onComplete({ test_id: testDef.id, measurements: next });
    }
  };

  const redo = () => {
    setResult(null);
    processedRef.current = false;
    tracker.startTest(testDef, currentSide);
  };

  // ── Pre-camera setup card ──────────────────────────────────────────────
  if (!started) {
    return (
      <div className="grid lg:grid-cols-2 gap-5" data-testid="mobility-live-setup">
        <div className="hpme-card p-5">
          <div className="hpme-card-corner" />
          <div className="flex items-center justify-between mb-1">
            <p className="hpme-label">{testDef.name}</p>
            <span className="text-xs font-mono px-2 py-0.5 rounded uppercase tracking-wider bg-primary/15 text-primary border border-primary/30">
              LIVE CAMERA
            </span>
          </div>
          <p className="text-sm text-zinc-200 mt-2 mb-1 font-medium">
            {testDef.side_setup_instruction?.[currentSide] || 'Set up as shown, then start the camera.'}
          </p>
          <div className="flex items-start gap-1.5 mb-4">
            <Camera className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
            <p className="text-xs text-zinc-400">
              Prop the phone so your <span className="text-primary font-semibold uppercase">whole body</span> is in frame, ~2-3m back.
            </p>
          </div>
          <Button className="hpme-btn-primary w-full" onClick={begin} data-testid="mobility-live-start-btn">
            <Camera className="w-5 h-5 mr-2" /> Start camera
          </Button>
          {testDef.video_query && (
            <a
              href={ytSearch(testDef.video_query)} target="_blank" rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-1.5 text-sm text-red-400 hover:text-red-300"
              data-testid="mobility-live-tutorial-link"
            >
              <Video className="w-4 h-4" /> Watch video tutorial
            </a>
          )}
          {onUseUpload && (
            <button
              type="button" onClick={onUseUpload}
              className="mt-3 w-full text-center text-xs text-zinc-500 hover:text-zinc-300"
              data-testid="mobility-live-switch-upload"
            >
              Prefer to film it yourself? Upload a clip instead →
            </button>
          )}
        </div>

        <div className="hpme-card p-5">
          <div className="hpme-card-corner" />
          <p className="hpme-label mb-3 flex items-center gap-1.5"><Hand className="w-3.5 h-3.5" /> How it works</p>
          <ol className="space-y-2" data-testid="mobility-live-howto">
            {(testDef.instructions || []).map((line, i) => (
              <li key={i} className="text-sm text-zinc-300 flex gap-2">
                <span className="text-primary font-mono text-xs mt-0.5">{i + 1}.</span> {line}
              </li>
            ))}
            <li className="text-sm text-primary flex gap-2 pt-1">
              <span className="font-mono text-xs mt-0.5">✓</span>
              Hold your end position steady — the ring fills and it captures automatically.
            </li>
          </ol>
        </div>
      </div>
    );
  }

  // ── Full-screen live camera ────────────────────────────────────────────
  const { PHASES } = tracker;
  const holdPct = Math.round((tracker.holdProgress || 0) * 100);
  const ringR = 34, ringC = 2 * Math.PI * ringR;
  const bk = scoreBucket(result?.score ?? null);
  const isRom = testDef.primary_metric?.mode === 'rom_from_neutral';
  const liveNum = isRom ? tracker.romValue : tracker.primaryAngle;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="mobility-live-fullscreen">
      {/* Camera + skeleton */}
      <div className="relative flex-1 min-h-0 bg-black overflow-hidden">
        <video ref={tracker.videoRef} className={`absolute inset-0 w-full h-full object-cover ${!tracker.cameraActive ? 'invisible' : ''}`} autoPlay playsInline muted />
        <canvas ref={tracker.canvasRef} className={`absolute inset-0 w-full h-full object-cover ${!tracker.cameraActive ? 'invisible' : ''}`} style={{ pointerEvents: 'none' }} />

        {/* Loading / starting */}
        {(tracker.initLoading || !tracker.cameraActive) && !tracker.cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-900/90 z-10 px-6 text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-zinc-400 text-sm">Starting camera & pose model…</p>
            {slowLoad && (
              <div className="mt-5" data-testid="mobility-live-slow">
                <p className="text-zinc-500 text-xs mb-3 max-w-xs">
                  Taking longer than usual. Make sure you allowed camera access — or film a clip instead.
                </p>
                {onUseUpload && (
                  <Button className="hpme-btn-primary" onClick={onUseUpload} data-testid="mobility-live-slow-upload">
                    Upload a clip instead
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
        {tracker.cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-900/95 z-10 p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mb-3" />
            <p className="text-zinc-300 text-sm mb-4">{tracker.cameraError}</p>
            {onUseUpload && (
              <Button className="hpme-btn-primary" onClick={onUseUpload} data-testid="mobility-live-error-upload">
                Upload a clip instead
              </Button>
            )}
          </div>
        )}

        {/* Top bar */}
        <div className="absolute top-0 inset-x-0 flex items-center justify-between p-4 pt-[max(1rem,env(safe-area-inset-top))] z-20 bg-gradient-to-b from-black/70 to-transparent">
          <button type="button" onClick={exit} className="w-9 h-9 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-white" data-testid="mobility-live-exit">
            <X className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="text-white font-secondary font-bold text-sm leading-tight">{testDef.name}</p>
            <p className="text-[10px] uppercase tracking-wider text-primary">{sideLabel(currentSide)}{sides.length > 1 ? ` · ${sideIdx + 1}/${sides.length}` : ''}</p>
          </div>
          <div className="w-9" />
        </div>

        {/* Coaching message */}
        {!result && tracker.cameraActive && (
          <div className="absolute top-20 inset-x-0 px-4 z-20 flex justify-center">
            <p className="text-center text-white text-sm bg-black/55 rounded-full px-4 py-2 max-w-md" data-testid="mobility-live-message">
              {tracker.message}
            </p>
          </div>
        )}

        {/* Positioning gate progress */}
        {tracker.phase === PHASES.POSITIONING && tracker.cameraActive && (
          <div className="absolute bottom-28 inset-x-0 px-8 z-20">
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden max-w-xs mx-auto">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.round((tracker.gateProgress || 0) * 100)}%` }} />
            </div>
            <p className="text-center text-[11px] text-zinc-300 mt-2">Detecting your position…</p>
          </div>
        )}

        {/* Live angle + holding ring (measuring) */}
        {tracker.phase === PHASES.MEASURING && !result && (
          <div className="absolute bottom-24 inset-x-0 z-20 flex flex-col items-center" data-testid="mobility-live-measuring">
            <div className="relative w-24 h-24">
              <svg className="w-24 h-24 -rotate-90" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r={ringR} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="6" />
                <circle
                  cx="40" cy="40" r={ringR} fill="none" stroke="#CCFF00" strokeWidth="6" strokeLinecap="round"
                  strokeDasharray={ringC}
                  strokeDashoffset={ringC - (ringC * (tracker.holdProgress || 0))}
                  style={{ transition: 'stroke-dashoffset 0.1s linear' }}
                  data-testid="mobility-live-hold-ring"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-white font-secondary font-bold text-2xl leading-none" data-testid="mobility-live-angle">
                  {liveNum != null ? `${Math.round(liveNum)}°` : '—'}
                </span>
                <span className="text-[9px] text-zinc-300 uppercase">{holdPct > 0 ? `hold ${holdPct}%` : (isRom ? 'range' : 'hold')}</span>
              </div>
            </div>
            <Button
              variant="outline" size="sm"
              onClick={tracker.captureNow}
              className="mt-4 bg-black/50 border-white/30 text-white"
              data-testid="mobility-live-capture-now"
            >
              Capture now
            </Button>
          </div>
        )}

        {/* Scoring spinner */}
        {scoring && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-30">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
          </div>
        )}
      </div>

      {/* Result panel */}
      {result && (
        <div className="bg-zinc-950 border-t border-zinc-800 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] z-40" data-testid="mobility-live-result">
          <div className="max-w-md mx-auto">
            {/* Score-first header — no raw degrees */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-sm text-zinc-300 truncate">{result.primary_label || testDef.primary_metric?.label}</span>
              </div>
              {result.score != null ? (
                <div className="text-right flex-shrink-0">
                  <span className="font-secondary font-bold text-3xl leading-none" style={{ color: toneHex(bk.tone) }} data-testid="mobility-live-result-score">
                    {result.score}<span className="text-base text-zinc-500">/100</span>
                  </span>
                  <span className={`block text-xs font-bold ${toneClass(bk.tone)}`} data-testid="mobility-live-result-band">{result.band || bk.label}</span>
                </div>
              ) : (
                <span className="font-secondary font-bold text-xl text-zinc-300" data-testid="mobility-live-result-captured">Captured</span>
              )}
            </div>

            {/* Plain-language "how we got this score" */}
            {result.explanation && (
              <div className="text-xs text-zinc-400 leading-relaxed mb-2 bg-zinc-900/60 rounded-sm p-2.5 border border-zinc-800" data-testid="mobility-live-explanation">
                <span className="text-zinc-500 uppercase tracking-wider text-[9px] block mb-1">How we got this score</span>
                {result.explanation}
              </div>
            )}

            {result.guard_violated && (
              <p className="text-xs text-yellow-400 mb-2 flex items-start gap-1.5" data-testid="mobility-live-guard">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                {result.guard_hint || 'Compensation detected — the score was adjusted. Redo for a cleaner read if you like.'}
              </p>
            )}
            <div className="flex gap-3">
              <Button onClick={accept} className="hpme-btn-primary flex-1" data-testid="mobility-live-accept">
                {sideIdx + 1 < sides.length
                  ? <>Looks good → {sideLabel(sides[sideIdx + 1])} <ArrowRight className="w-4 h-4 ml-1" /></>
                  : <>Looks good → save <CheckCircle className="w-4 h-4 ml-1" /></>}
              </Button>
              <Button onClick={redo} variant="outline" className="flex-1" data-testid="mobility-live-redo">
                <RotateCcw className="w-4 h-4 mr-1" /> Redo
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentLiveView;
